/**
 * THE RETIRED `/admin` ROUTES (economics-pulse D12, th-l19q2.25; spec
 * `economics-pulse-surface` § "The admin route retires into Pulse with every
 * live behavior re-homed", scenario "A bookmark still lands somewhere useful").
 *
 * `/admin` was a page. Everything it did now lives in Analyze › Pulse — the
 * stats as health cards, the queues and the people roster as Pulse pages — so
 * the five old paths are no longer pages of their own. They are BOOKMARKS, and
 * a bookmark that 404s is the one outcome the spec rules out: each one lands on
 * Pulse, the three that named a queue with that queue's page open.
 *
 * ── WHY THE GUARD LOADS THE LANDING ─────────────────────────────────────────
 * Each record's guard lands through `landOnPulse` (`router/pulseLanding.js`),
 * the one landing the admin menu and the inbox share; that module carries why
 * a landing writes the stores and returns `MainMap`. It writes workspace
 * stores, and `router/index.js` imports this file statically, so the guard
 * loads it when it runs (launch-marketing-pages M2, the boot-graph rule).
 *
 * ── WHY THE RECORDS CARRY NO NAME ───────────────────────────────────────────
 * A route name exists so code can PUSH a destination without spelling its path.
 * Nothing pushes these: `UserMenu.vue` calls `landOnPulse` directly, and the
 * page tree whose tab and card links did push them —
 * `views/AdminDashboard.vue` and `views/admin/*` — is deleted (th-l19q2.16).
 * These five records exist for
 * typed and bookmarked PATHS only, so a name here would be a second way to
 * reach a destination that has one, and the first thing a later author would
 * reach for instead of `landOnPulse`.
 *
 * No record imports a view either: the `component` below is the placeholder the
 * guard never renders, exactly as the `/tours/:id` record spells it.
 */

/**
 * The retirement table: one row per retired path, carrying the Pulse page the
 * path's page became. `null` is the Pulse index — the two paths that were the
 * dashboard itself.
 *
 * The page keys are page keys of the Pulse roster (`utils/pulsePages.js`), the
 * vocabulary the one Pulse opener resolves; a key off the roster would land on
 * the index.
 */
export const RETIRED_ADMIN_ROUTES = Object.freeze([
  { path: "/admin", page: null },
  { path: "/admin/dashboard", page: null },
  { path: "/admin/validations", page: "validations" },
  { path: "/admin/people", page: "people" },
  { path: "/admin/confidential-citations", page: "confidential_citations" },
]);

/**
 * The five route records, built from the one table.
 *
 * `meta.requiresAuth` is kept so the global guard still sends a signed-out
 * visitor to sign-in with the path preserved — they come back to the same
 * page. A bare `redirect:` record would skip the global guard entirely, which
 * is why these resolve through `beforeEnter` instead. `meta.workspace` marks
 * each as a record that renders `MainView`, as every such record declares
 * (`router/index.js`).
 *
 * @param {Function} placeholderComponent — the workspace's lazy `MainView`
 *   loader (`router/index.js`), never rendered: the guard always redirects. A
 *   record with neither component nor children warns at build.
 * @returns {Array<object>} vue-router route records.
 */
export function retiredAdminRouteRecords(placeholderComponent) {
  return RETIRED_ADMIN_ROUTES.map(({ path, page }) => ({
    path,
    meta: { requiresAuth: true, workspace: true },
    beforeEnter: async () => {
      const { landOnPulse } = await import("@/router/pulseLanding");
      return landOnPulse(page);
    },
    component: placeholderComponent,
  }));
}
